import { normalize } from 'normalizr'
import { Schemas } from 'redux-modules/schema'
import { updateEntities } from 'redux-modules/entity'

import api from 'api'

const FETCH_REQUEST = 'realhub/order/FETCH_REQUEST'
const FETCH_SUCCESS = 'realhub/order/FETCH_SUCCESS'
const FETCH_FAILURE = 'realhub/order/FETCH_FAILURE'
const CREATE_REQUEST = 'realhub/order/CREATE_REQUEST'
const CREATE_SUCCESS = 'realhub/order/CREATE_SUCCESS'
const CREATE_FAILURE = 'realhub/order/CREATE_FAILURE'
const UPDATE_REQUEST = 'realhub/order/UPDATE_REQUEST'
const UPDATE_SUCCESS = 'realhub/order/UPDATE_SUCCESS'
const UPDATE_FAILURE = 'realhub/order/UPDATE_FAILURE'
const APPROVE_REQUEST = 'realhub/order/APPROVE_REQUEST'
const APPROVE_SUCCESS = 'realhub/order/APPROVE_SUCCESS'
const APPROVE_FAILURE = 'realhub/order/APPROVE_FAILURE'
const SEARCH_REQUEST = 'realhub/order/SEARCH_REQUEST'
const SEARCH_SUCCESS = 'realhub/order/SEARCH_SUCCESS'
const SEARCH_FAILURE = 'realhub/order/SEARCH_FAILURE'
const UPDATE_FILTER = 'realhub/order/UPDATE_FILTER'

// Initial State
const initialState = {
  result: [],
  loaded: false,
  loadedForEntities: [],
  loadedOrdersForAgencyIds: [],
  loading: false,
  creating: false,
  updating: false,
  approving: false,
  filter: '',
  errors: [],
}

// Actions
export function fetchRequest(payload = {}){
  return {
    type: FETCH_REQUEST,
    loadedForEntities: payload.loadedForEntities,
    loadedOrdersForAgencyIds: payload.loadedOrdersForAgencyIds,
  }
}

export function fetchSuccess(payload = {}){
  return {
    type: FETCH_SUCCESS,
    payload,
  }
}

export function fetchFailure(errors = []){
  return {
    type: FETCH_FAILURE,
    errors,
  }
}

export function createRequest(){
  return {
    type: CREATE_REQUEST,
  }
}

export function createSuccess(){
  return {
    type: CREATE_SUCCESS,
  }
}

export function createFailure(errors = []){
  return {
    type: CREATE_FAILURE,
    errors,
  }
}

export function updateRequest(){
  return {
    type: UPDATE_REQUEST,
  }
}

export function updateSuccess(){
  return {
    type: UPDATE_SUCCESS,
  }
}

export function updateFailure(errors = []){
  return {
    type: UPDATE_FAILURE,
    errors,
  }
}

export function approveRequest(){
  return {
    type: APPROVE_REQUEST,
  }
}

export function approveSuccess(){
  return {
    type: APPROVE_SUCCESS,
  }
}

export function approveFailure(errors = []){
  return {
    type: APPROVE_FAILURE,
    errors,
  }
}

export function searchRequest(){
  return {
    type: SEARCH_REQUEST,
  }
}

export function searchSuccess(payload = {}){
  return {
    type: SEARCH_SUCCESS,
    payload,
  }
}

export function searchFailure(errors = []){
  return {
    type: SEARCH_FAILURE,
    errors,
  }
}

export function filterOrders(query){
  return {
    type: UPDATE_FILTER,
    payload: query,
  }
}

// Helper Functions
const handleError = errors => errors || ['Something went wrong']

// Action Creators
export function loadOrders(options, callback){
  return (dispatch, getState) => {
    const loadedOrdersForAgencyIds = getState().orders.loadedOrdersForAgencyIds.slice()
    if (options.agency_id && !loadedOrdersForAgencyIds.includes(options.agency_id)){
      loadedOrdersForAgencyIds.push(options.agency_id)
    }

    const loadedForEntities = [...getState().orders.loadedForEntities]
    if (options.entityKey && !loadedForEntities.includes(options.entityKey)){
      loadedForEntities.push(options.entityKey)
    }

    // Set Loading
    dispatch(fetchRequest({ loadedForEntities, loadedOrdersForAgencyIds }))

    const promise = api('/admin/orders.json', options)
      .then(response => response.json().then(json => ({ json, response })))
      .then(({ json, response }) => {
        if (!response.ok){
          return Promise.reject(json)
        }

        const normalizedJson = normalize(json, Schemas.ORDER_ARRAY)

        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess(normalizedJson.result))

        if (callback) callback()

        return json
      })
      .catch((json) => {
        const errors = handleError(json.errors)
        dispatch(fetchFailure(errors))
      })

    return promise
  }
}

export function loadOrder(orderId, options, callback){
  return (dispatch) => {
    dispatch(fetchRequest())

    const promise = api(`/admin/orders/${orderId}.json`, options)
      .then(response => response.json().then(json => ({ json, response })))
      .then(({ json, response }) => {
        if (!response.ok){
          return Promise.reject(json)
        }

        const normalizedJson = normalize(json, Schemas.ORDER)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        if (callback){
          callback(json)
        }

        return json
      })
      .catch((json) => {
        const errors = handleError(json.errors)
        dispatch(fetchFailure(errors))
      })

    return promise
  }
}

export function createOrder(order, options, callback){
  const config = {
    method: 'POST',
    body: JSON.stringify({
      order,
    }),
  }

  return (dispatch) => {
    dispatch(createRequest())

    const promise = api('/admin/orders.json', options, config)
      .then(response => response.json().then(json => ({ json, response })))
      .then(({ json, response }) => {
        if (!response.ok){
          return Promise.reject(json)
        }

        const normalizedJson = normalize(json, Schemas.ORDER)
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        if (callback) callback(json)

        return json
      })
      .catch((json) => {
        const errors = handleError(json.errors)
        dispatch(createFailure(errors))
      })

    return promise
  }
}

export function updateOrder(order, options, callback){
  const config = {
    method: 'PUT',
    body: JSON.stringify({
      order,
    }),
  }

  return (dispatch) => {
    // Set Loading
    dispatch(updateRequest())

    const promise = api(`/admin/orders/${order.id}.json`, options, config)
      .then(response => response.json().then(json => ({ json, response })))
      .then(({ json, response }) => {
        if (!response.ok){
          return Promise.reject(json)
        }

        const normalizedJson = normalize(json, Schemas.ORDER)
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        if (callback) callback()

        return json
      })
      .catch((json) => {
        const errors = handleError(json.errors)
        dispatch(updateFailure(errors))
      })

    return promise
  }
}

export function bulkUpdateOrderItemStatuses(order, status, options, callback){
  const config = {
    method: 'POST',
    body: JSON.stringify({
      order_id: order.id,
      status_id: status.id,
    }),
  }

  return (dispatch) => {
    dispatch(updateRequest())

    const promise = api(`/admin/orders/${order.id}/bulk_status_update.json`, options, config)
      .then(response => response.json().then(json => ({ json, response })))
      .then(({ json, response }) => {
        if (!response.ok){
          return Promise.reject(json)
        }

        const normalizedJson = normalize(json, Schemas.ORDER)
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        if (callback) callback(json)

        return json
      })
      .catch((json) => {
        const errors = handleError(json.errors)
        dispatch(updateFailure(errors))
      })

    return promise
  }
}

export function approveOrder(order, options, callback){
  const config = {
    method: 'PUT',
    body: JSON.stringify({
      order,
    }),
  }

  return (dispatch) => {
    // Set Loading
    dispatch(approveRequest())

    const promise = api(`/admin/orders/${order.id}/approve.json`, options, config)
      .then(response => response.json().then(json => ({ json, response })))
      .then(({ json, response }) => {
        if (!response.ok){
          return Promise.reject(json)
        }

        const normalizedJson = normalize(json, Schemas.ORDER)
        dispatch(updateEntities(normalizedJson))
        dispatch(approveSuccess())

        if (callback){
          callback(json)
        }
      })
      .catch((json) => {
        const errors = handleError(json.errors)
        dispatch(approveFailure(errors))
      })

    return promise
  }
}

export function unapproveOrder(order, options, callback){
  const config = {
    method: 'PUT',
    body: JSON.stringify({
      order,
    }),
  }

  return (dispatch) => {
    // Set Loading
    dispatch(approveRequest())

    const promise = api(`/admin/orders/${order.id}/unapprove.json`, options, config)
      .then(response => response.json().then(json => ({ json, response })))
      .then(({ json, response }) => {
        if (!response.ok){
          return Promise.reject(json)
        }

        const normalizedJson = normalize(json, Schemas.ORDER)
        dispatch(updateEntities(normalizedJson))
        dispatch(approveSuccess())

        if (callback){
          callback(json)
        }
      })
      .catch((json) => {
        const errors = handleError(json.errors)
        dispatch(approveFailure(errors))
      })

    return promise
  }
}

export function searchOrders(options, callback){
  return (dispatch) => {
    dispatch(searchRequest())

    const promise = api('/admin/orders/search.json', options)
      .then(response => response.json().then(json => ({ json, response })))
      .then(({ json, response }) => {
        if (!response.ok){
          return Promise.reject(json)
        }

        const normalizedJson = normalize(json, Schemas.ORDER_ARRAY)
        dispatch(updateEntities(normalizedJson))
        dispatch(searchSuccess(normalizedJson.result))

        if (callback) callback()

        return json
      })
      .catch((json) => {
        const errors = handleError(json.errors)
        dispatch(searchFailure(errors))
      })

    return promise
  }
}

export function createOrderDeliveryTask(order, options, callback){
  const config = {
    method: 'POST',
    body: JSON.stringify({
      order_id: order.id,
    }),
  }

  return (dispatch) => {
    dispatch(createRequest())

    const promise = api(`/admin/orders/${order.id}/create_delivery_task.json`, options, config)
      .then(response => response.json().then(json => ({ json, response })))
      .then(({ json, response }) => {
        if (!response.ok){
          return Promise.reject(json)
        }

        const normalizedJson = normalize(json, Schemas.ORDER_ITEM_TASK)
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        if (callback) callback(json)

        return json
      })
      .catch((json) => {
        const errors = handleError(json.errors)
        dispatch(createFailure(errors))
      })

    return promise
  }
}

export function reprocessOrderApiJob(order, options, callback){
  const config = {
    method: 'POST',
    body: JSON.stringify({
      order_id: order.id,
    }),
  }

  return (dispatch) => {
    dispatch(createRequest())

    const promise = api(`/admin/orders/${order.id}/reprocess_api_job.json`, options, config)
      .then(response => response.json().then(json => ({ json, response })))
      .then(({ json, response }) => {
        if (!response.ok){
          return Promise.reject(json)
        }

        if (callback) callback(json)

        return json
      })
      .catch((json) => {
        const errors = handleError(json.errors)
        dispatch(createFailure(errors))
      })

    return promise
  }
}

// Reducer
export default function reducer(state = initialState, action = {}){
  const { payload, type } = action

  switch (type){
    case FETCH_REQUEST:
      return {
        ...state,
        loading: true,
        loadedForEntities: action.loadedForEntities || state.loadedForEntities,
        loadedOrdersForAgencyIds: action.loadedOrdersForAgencyIds || state.loadedOrdersForAgencyIds,
      }
    case FETCH_SUCCESS:
      return {
        ...state,
        loading: false,
        loaded: true,
        result: payload,
        errors: [],
      }
    case FETCH_FAILURE:
      return {
        ...state,
        loading: false,
        loaded: false,
        errors: action.errors,
      }
    case CREATE_REQUEST:
      return {
        ...state,
        creating: true,
      }
    case CREATE_SUCCESS:
      return {
        ...state,
        creating: false,
        errors: [],
      }
    case CREATE_FAILURE:
      return {
        ...state,
        creating: false,
        errors: action.errors,
      }
    case UPDATE_REQUEST:
      return { ...state, updating: true }
    case UPDATE_SUCCESS:
      return {
        ...state,
        updating: false,
        errors: [],
      }
    case UPDATE_FAILURE:
      return {
        ...state,
        updating: false,
        errors: action.errors,
      }
    case APPROVE_REQUEST:
      return { ...state, approving: true }
    case APPROVE_SUCCESS:
      return {
        ...state,
        approving: false,
        errors: [],
      }
    case APPROVE_FAILURE:
      return {
        ...state,
        approving: false,
        errors: action.errors,
      }
    case SEARCH_REQUEST:
      return { ...state, loading: true }
    case SEARCH_SUCCESS:
      return {
        ...state,
        loading: false,
        loaded: true,
        result: payload,
        errors: [],
      }
    case SEARCH_FAILURE:
      return {
        ...state,
        loading: false,
        loaded: false,
        errors: action.errors,
      }
    case UPDATE_FILTER:
      return {
        ...state,
        filter: payload,
      }
    default:
      return state
  }
}
